/**
 * The payee naming mode (TMMAPP-0163 item 6; TMMAPI-0193 U3, `/me.payeeNamingMode`): **Free matching
 * first, then AI for the rest** (recommended, the default) or **Use AI for all naming**. One choice,
 * three places: Settings › AI Automation (`NamingModeSetting`), the coverage card's link and the
 * naming dialog's line (`NamingModeDialog` / the dialog's in-place body, `NamingModeChoice`).
 *
 * Saving is `PATCH /v1/me {payeeNamingMode}` and nothing else: switching renames nothing and never
 * starts a run. A switch to AI_ALL answers `runSuggested`, and the host offers **Name them with AI
 * now**, which opens the naming dialog (the person still presses Continue). For a GUEST or NONE tier
 * every control goes through `useSubscriberAction()`: the purchase experience, never a disabled
 * control (TMMAPP-0162 item 2).
 */
import { useId, useRef, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PayeeNamingMode } from '@/api/me';
import { SubmitButton } from '@/components/SubmitButton';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { NAME_WITH_AI_STATE, switchedCopyKey, useNamingModeSave } from './namingMode';

const MODES = [
  { value: 'FREE_FIRST', label: 'naming.mode.freeFirst', help: 'naming.mode.freeFirstHelp' },
  { value: 'AI_ALL', label: 'naming.mode.aiAll', help: 'naming.mode.aiAllHelp' },
] as const satisfies readonly { value: PayeeNamingMode; label: string; help: string }[];

/** The two choices, each with its one-line help (the radio's description). */
export function NamingModeChoice({
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
}: {
  value: PayeeNamingMode;
  onChange: (mode: PayeeNamingMode, event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean | undefined;
  /** The heading that names the group. */
  labelledBy: string;
  /** A description of the whole group (the subscribe hint for a GUEST or NONE tier). */
  describedBy?: string | undefined;
}) {
  const { t } = useTranslation();
  const base = useId();
  return (
    <RadioGroup
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      value={value}
      data-naming-mode=""
      onChange={(event, next) => {
        const mode = MODES.find((entry) => entry.value === next)?.value;
        // `useSubscriberAction` takes the change's event as its own (the established cast).
        if (mode !== undefined) onChange(mode, event as unknown as MouseEvent<HTMLButtonElement>);
      }}
    >
      {MODES.map((mode) => (
        <Box key={mode.value} sx={{ mb: 1 }}>
          <FormControlLabel
            value={mode.value}
            disabled={disabled}
            control={
              <Radio slotProps={{ input: { 'aria-describedby': `${base}-${mode.value}` } }} />
            }
            label={t(mode.label)}
          />
          <Typography
            id={`${base}-${mode.value}`}
            variant="body2"
            sx={{ color: 'text.secondary', paddingInlineStart: 4 }}
          >
            {t(mode.help)}
          </Typography>
        </Box>
      ))}
    </RadioGroup>
  );
}

/**
 * The choice as its own body (the small confirm dialog, or the naming dialog's swap): the person
 * picks, then Save. `initial` is the choice shown first (the card's link offers AI_ALL).
 */
export function NamingModeBody({
  initial,
  titleId,
  onCancel,
  onSaved,
  cancelLabel,
}: {
  initial: PayeeNamingMode;
  titleId: string;
  onCancel: () => void;
  onSaved: (mode: PayeeNamingMode, runSuggested: boolean) => void;
  cancelLabel?: string | undefined;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const [choice, setChoice] = useState<PayeeNamingMode>(initial);
  const { save, busy, error } = useNamingModeSave();
  const unchanged = choice === me.user.payeeNamingMode;

  async function handleSave() {
    if (unchanged) {
      onCancel();
      return;
    }
    const result = await save(choice);
    if (result !== null) onSaved(choice, result.runSuggested);
  }

  return (
    <>
      <DialogTitle id={titleId}>{t('naming.mode.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <NamingModeChoice
            value={choice}
            disabled={busy}
            labelledBy={titleId}
            onChange={(mode) => setChoice(mode)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <SubmitButton onClick={() => void handleSave()} loading={busy} disabled={busy}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

/** The coverage card's small confirm dialog. */
export function NamingModeDialog({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: PayeeNamingMode;
  onClose: () => void;
  onSaved: (mode: PayeeNamingMode, runSuggested: boolean) => void;
}) {
  const titleId = useId();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      {open && (
        <NamingModeBody initial={initial} titleId={titleId} onCancel={onClose} onSaved={onSaved} />
      )}
    </Dialog>
  );
}

/**
 * Settings › AI Automation's "How should we name payees?" (item 6). A choice saves at once, as the
 * tab's switches do; a switch to AI_ALL offers **Name them with AI now**, which goes to Payees and
 * opens the naming dialog there.
 */
export function NamingModeSetting({
  onNotify,
}: {
  onNotify: (message: string, severity?: 'success' | 'error') => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const titleId = useId();
  const { save, busy, error } = useNamingModeSave();
  const [offerRun, setOfferRun] = useState(false);
  // The mode a change asks for, read by the subscriber action it runs.
  const pending = useRef<PayeeNamingMode | null>(null);
  const change = useSubscriberAction(() => {
    const mode = pending.current;
    if (mode === null) return;
    void save(mode).then((result) => {
      if (result === null) return;
      setOfferRun(result.runSuggested);
      // The offer says it in place; a switch without one says it as a notice.
      if (!result.runSuggested) onNotify(t(switchedCopyKey(mode)), 'success');
    });
  });

  return (
    <Box data-naming-mode-setting="">
      <Typography id={titleId} variant="h3" gutterBottom>
        {t('naming.mode.title')}
      </Typography>
      {error !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <NamingModeChoice
        value={me.user.payeeNamingMode}
        disabled={busy}
        labelledBy={titleId}
        describedBy={change.descriptionId}
        onChange={(mode, event) => {
          pending.current = mode;
          change.run(event);
        }}
      />
      <SubscriberActionHint id={change.descriptionId} description={change.description} />
      {offerRun && (
        <Alert
          severity="info"
          data-name-now-offer=""
          action={
            <Button
              size="small"
              onClick={() => navigate('/me/payees?tab=review', { state: NAME_WITH_AI_STATE })}
            >
              {t('naming.mode.nameNow')}
            </Button>
          }
        >
          {t('naming.mode.switchedAiAll')}
        </Alert>
      )}
    </Box>
  );
}
